/**
 * FilterBar — reusable filter-pill layout for list pages.
 *
 * Usage:
 *   <FilterBar>
 *     <FilterPill
 *       ariaLabel="Filter by status"
 *       value={status}
 *       onValueChange={setStatus}
 *       options={[
 *         { value: "open", label: "Open" },
 *         { value: "closed", label: "Closed" },
 *       ]}
 *     />
 *     <FilterPill
 *       ariaLabel="Filter by severity"
 *       placeholder="Severity"
 *       value={severity}
 *       onValueChange={setSeverity}
 *       onRemove={() => removeFilter("severity")}
 *       options={severityOptions}
 *     />
 *     <AddFilterMenu
 *       options={[
 *         { id: "severity", label: "Severity" },
 *         { id: "persona", label: "Persona" },
 *       ]}
 *       excluded={visibleFilters}
 *       onSelect={(id) => addFilter(id)}
 *     />
 *   </FilterBar>
 */

import type { ReactNode } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { SimpleTooltip } from "@/components/ui/tooltip";

export interface FilterOption<T extends string = string> {
  value: T;
  label: string;
}

export function FilterBar({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      {children}
    </div>
  );
}

interface FilterPillProps<T extends string> {
  ariaLabel: string;
  value: T;
  onValueChange: (value: T) => void;
  options: ReadonlyArray<FilterOption<T>>;
  /** Placeholder text shown when value doesn't match any option. */
  placeholder?: string;
  /** When provided, renders a remove (×) button. Omit for always-visible filters. */
  onRemove?: () => void;
  /** Constrain the trigger width — useful for long labels (e.g. walkthrough names). */
  maxWidth?: string;
}

export function FilterPill<T extends string>({
  ariaLabel,
  value,
  onValueChange,
  options,
  placeholder,
  onRemove,
  maxWidth,
}: FilterPillProps<T>) {
  if (onRemove) {
    return (
      <div
        className={cn(
          "group inline-flex h-9 items-center rounded-sm border border-input bg-background shadow-xs transition-colors hover:border-foreground/30 focus-within:border-ring focus-within:ring-1 focus-within:ring-ring",
          maxWidth,
        )}
      >
        <Select value={value} onValueChange={onValueChange}>
          <SelectTrigger
            size="none"
            className="h-full w-auto gap-1.5 rounded-l-sm rounded-r-none border-0 bg-transparent pl-3 pr-2 text-sm font-normal shadow-none hover:bg-muted/50 focus-visible:ring-0 focus-visible:outline-none"
            aria-label={ariaLabel}
          >
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="h-4 w-px bg-border shrink-0" aria-hidden="true" />
        <SimpleTooltip
          content={`Remove ${ariaLabel.replace(/^Filter by /, "")} filter`}
        >
          <button
            type="button"
            className="flex h-full w-8 items-center justify-center rounded-r-sm text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:rounded-r-sm cursor-pointer shrink-0"
            onClick={onRemove}
            aria-label={`Remove ${ariaLabel.replace(/^Filter by /, "")} filter`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </SimpleTooltip>
      </div>
    );
  }

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        size="none"
        className={cn(
          "h-9 w-auto gap-1.5 rounded-sm border-input bg-background px-3 text-sm font-normal shadow-xs transition-colors hover:bg-muted/50 hover:border-foreground/40",
          maxWidth,
        )}
        aria-label={ariaLabel}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface AddFilterMenuProps<T extends string> {
  options: ReadonlyArray<{ id: T; label: string }>;
  /** IDs already visible — hidden from the menu. */
  excluded?: ReadonlySet<T>;
  onSelect: (id: T) => void;
}

export function AddFilterMenu<T extends string>({
  options,
  excluded,
  onSelect,
}: AddFilterMenuProps<T>) {
  const available = excluded
    ? options.filter((o) => !excluded.has(o.id))
    : options;

  if (available.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-9 gap-1.5 rounded-sm text-muted-foreground"
        >
          <Plus className="h-3.5 w-3.5" />
          Add filter
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {available.map((opt) => (
          <DropdownMenuItem key={opt.id} onClick={() => onSelect(opt.id)}>
            {opt.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
